// components/Hero.js - FINAL FIXED VERSION
import React, { useState, useEffect, useCallback, useMemo } from 'react';

const Hero = () => {
  // Memoize slides array to prevent unnecessary re-renders
  const slides = useMemo(() => [
    {
      id: 1,
      backgroundImage: '/images/1.jpg', // Local image path
      title: "Harnessing Solar Power for a Sustainable Future",
      subtitle: "Leading provider of solar energy solutions in Malawi and beyond, delivering professional, reliable solar systems since 2018."
    },
    {
      id: 2,
      backgroundImage: '/images/2.jpg', // Local image path
      title: "Innovative Solar Solutions for Every Need",
      subtitle: "From residential installations to large-scale commercial projects, we deliver cutting-edge solar technology."
    },
    {
      id: 3,
      backgroundImage: '/images/13.jpg', // Local image path
      title: "Powering Malawi's Growth with Renewable Energy",
      subtitle: "Registered with Malawi Energy Regulatory Authority (MERA) and trusted by government and international organizations."
    },
    {
      id: 4,
      backgroundImage: '/images/16.jpg', // Local image path
      title: "Expert Solar Installation & Maintenance",
      subtitle: "Our team of certified engineers ensures flawless installation and ongoing support for all solar systems."
    },
    {
      id: 5,
      backgroundImage: '/images/5.jpg', // Local image path
      title: "Your Trusted Solar Energy Partner",
      subtitle: "Committed to excellence in solar system design, installation, and after-sales service across Malawi."
    }
  ], []);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [fade, setFade] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [showSlideshow, setShowSlideshow] = useState(false);

  // Preload images for smoother transitions
  useEffect(() => {
    let loadedCount = 0;
    
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          loadedCount++;
          setImagesLoaded(loadedCount);
          resolve();
        };
        img.onerror = () => {
          console.error(`Failed to load image: ${src}`);
          loadedCount++;
          setImagesLoaded(loadedCount);
          resolve();
        };
      });
    };

    const preloadImages = async () => {
      const promises = slides.map(slide => loadImage(slide.backgroundImage));
      await Promise.all(promises);
      setShowSlideshow(true);
    };

    preloadImages();
  }, [slides]);

  // Auto slide transition
  useEffect(() => {
    if (!showSlideshow) return;

    const interval = setInterval(() => {
      setFade(false);
      
      setTimeout(() => {
        setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
        setFade(true);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  }, [showSlideshow, slides.length]);

  // Manual slide navigation functions
  const goToSlide = useCallback((index) => {
    setFade(false);
    setTimeout(() => {
      setCurrentSlide(index);
      setFade(true);
    }, 300);
  }, []);

  const goToNextSlide = useCallback(() => {
    setFade(false);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setFade(true);
    }, 300);
  }, [slides.length]);

  const goToPrevSlide = useCallback(() => {
    setFade(false);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setFade(true);
    }, 300);
  }, [slides.length]);

  // If images haven't loaded yet, show loading state
  if (!showSlideshow) {
    return (
      <section id="home" className="hero">
        <div className="hero-overlay">
          <div className="container">
            <div className="hero-content">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading images... {imagesLoaded}/{slides.length}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="home" className="hero">
      {/* Background Slideshow */}
      <div className="hero-slideshow">
        {slides.map((slide, index) => (
          <div 
            key={slide.id}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ 
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${slide.backgroundImage})` 
            }}
          />
        ))}
      </div>

      {/* Slide Indicators */}
      <div className="slide-indicators">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            className={`indicator ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <button className="slide-nav prev" onClick={goToPrevSlide} aria-label="Previous slide">
        <span>&#8249;</span>
      </button>
      <button className="slide-nav next" onClick={goToNextSlide} aria-label="Next slide">
        <span>&#8250;</span>
      </button>

      {/* Hero Content */}
      <div className="hero-overlay">
        <div className="container">
          <div className={`hero-content ${fade ? 'fade-in' : 'fade-out'}`}>
            <h1>{slides[currentSlide].title}</h1>
            <p>{slides[currentSlide].subtitle}</p>
            <div className="hero-buttons">
              <a href="#contact" className="btn">Get In Touch</a>
              <a href="#projects" className="btn btn-outline">Our Projects</a>
            </div>
          </div>
          
          {/* Slide Progress Bar */}
          <div className="slide-progress">
            <div 
              className="progress-bar" 
              style={{
                animation: `progress ${5000}ms linear infinite`,
                animationPlayState: 'running'
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;